/**
 * App-wide double-click / double-submit protection.
 *  1. A button (or submit input / form) clicked again within GUARD_MS is ignored.
 *  2. fetch(): the same POST / PUT / PATCH / DELETE (same url + body) sent while the first is still
 *     running is not sent again – it gets the first one's response.
 * (axios requests are guarded in utils/helper.jsx)
 * A button can opt out with data-allow-double-click="true".
 */
const GUARD_MS = 1000;
const lastClick = new WeakMap();

// ---- busy spinner ---------------------------------------------------------
// A save / update / delete request sent right after a button click shows a spinner on that button
// until the answer comes. Only an attribute is set on the button (React keeps working normally).
// A button can opt out with data-no-spinner="true".
const BUSY_LINK_MS = 1500;   // request must start within 1.5 s of the click
let lastButton = null;
let lastButtonAt = 0;
const busyCount = new WeakMap();

function injectBusyStyle() {
  if (document.getElementById("sg-busy-style")) return;
  const st = document.createElement("style");
  st.id = "sg-busy-style";
  st.textContent = `
    [data-sg-busy] { pointer-events: none; opacity: .8; cursor: progress; }
    [data-sg-busy]::before {
      content: ""; display: inline-block; width: .85em; height: .85em; margin-right: .4em;
      vertical-align: -0.1em; border: .15em solid currentColor; border-right-color: transparent;
      border-radius: 50%; animation: sg-spin .7s linear infinite;
    }
    @keyframes sg-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(st);
}

/** call when a save request starts; returns the function to call when it ends */
export function beginBusy() {
  const btn = lastButton;
  if (!btn || Date.now() - lastButtonAt > BUSY_LINK_MS || !document.contains(btn)) return () => {};
  if (btn.dataset.noSpinner === "true") return () => {};
  const n = (busyCount.get(btn) || 0) + 1;
  busyCount.set(btn, n);
  if (n === 1) btn.setAttribute("data-sg-busy", "1");
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    const k = (busyCount.get(btn) || 1) - 1;
    if (k <= 0) {
      busyCount.delete(btn);
      btn.removeAttribute("data-sg-busy");
    } else busyCount.set(btn, k);
  };
  setTimeout(end, 30000); // safety
  return end;
}

function clickGuard(e) {
  const el = e.target && e.target.closest && e.target.closest('button, input[type="submit"], input[type="button"], a.btn, [role="button"]');
  if (!el || el.dataset.allowDoubleClick === "true") return;
  const now = Date.now();
  const prev = lastClick.get(el) || 0;
  if (now - prev < GUARD_MS) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return;
  }
  lastClick.set(el, now);
  lastButton = el;
  lastButtonAt = now;
}

const lastSubmit = new WeakMap();
function submitGuard(e) {
  const form = e.target;
  if (!form || form.dataset?.allowDoubleSubmit === "true") return;
  const now = Date.now();
  if (now - (lastSubmit.get(form) || 0) < GUARD_MS) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return;
  }
  lastSubmit.set(form, now);
}

function guardFetch() {
  if (typeof window === "undefined" || !window.fetch || window.fetch.__guarded) return;
  const original = window.fetch.bind(window);
  const running = new Map();
  const bodyKey = (body) => {
    try {
      if (body == null) return "";
      if (typeof body === "string") return body;
      if (typeof FormData !== "undefined" && body instanceof FormData) {
        const parts = [];
        body.forEach((v, k) => parts.push(`${k}=${v && typeof v === "object" ? `${v.name}:${v.size}` : v}`));
        return parts.join("&");
      }
      return null; // blobs / streams: do not guard
    } catch (e) {
      return null;
    }
  };
  const guarded = (input, init = {}) => {
    const method = String(init.method || (input && input.method) || "GET").toUpperCase();
    if (!["POST", "PUT", "PATCH", "DELETE"].includes(method)) return original(input, init);
    const b = bodyKey(init.body);
    if (b === null) return original(input, init);
    const url = typeof input === "string" ? input : input && input.url;
    const key = `${method} ${url} ${b}`;
    const first = running.get(key);
    if (first) return first.then((r) => r.clone());
    const endBusy = beginBusy();
    const p = original(input, init);
    p.then(endBusy, endBusy);
    running.set(key, p);
    const done = () => { if (running.get(key) === p) running.delete(key); };
    p.then(done, done);
    setTimeout(done, 30000); // safety: never block the same request longer than 30 s
    return p.then((r) => r.clone());
  };
  guarded.__guarded = true;
  window.fetch = guarded;
}

export function installSubmitGuard() {
  if (typeof document === "undefined" || window.__submitGuardInstalled) return;
  window.__submitGuardInstalled = true;
  injectBusyStyle();
  document.addEventListener("click", clickGuard, true);
  document.addEventListener("submit", submitGuard, true);
  guardFetch();
}

export default installSubmitGuard;
